import { useEffect, useMemo, useState } from 'react';
import { Alert } from 'react-native';
import {
  Flex,
  GoodsCard,
  GoodsCardSkeletonList,
  List,
  PrimaryButton,
  SafeAreaBottomSpacing,
  Spinner,
  StackScreen,
  Typography,
  useScreenSize,
} from '@banhala/ui-kit';
import { useHandleBackPressed } from 'shared/src/hooks';
import { AnalyticsScreen } from 'shared/src/context';
import { bridge, localizeNumber } from 'shared/src/utils';
import { useHomeRoute } from '../../hooks/useHomeRoute';
import { useDeleteLikedGoods, useLikedGoods } from '../Like/queries';
import {
  convertLikedGoodsItemToGoodsData,
  type LikedGoodsCardItem,
} from '../Like/components/LikeGoods/utils/convertLikedGoodsItemToGoodsData';
import { OverlayCheckBoxView } from './components/OverlayCheckBoxView';
import { useLogCompleteRemoveGoods } from './hooks/useLogCompleteRemoveGoods';
import { useLogViewLikedGoodsEdit } from './hooks/useLogViewLikedGoodsEdit';

const SCREEN = 'LIKED_GOODS_EDIT';

const COLUMN_GAP = 2;
const COLUMNS = 3;

type EditRow =
  | { type: 'header'; key: 'header'; totalCount: number }
  | { type: 'goods'; key: string; items: LikedGoodsCardItem[] };

export const EditGoodsLikeScreen = () => {
  const { showBackButton, onPressBack } = useHandleBackPressed();
  const { width: screenWidth } = useScreenSize();
  const itemWidth = (screenWidth - COLUMN_GAP * (COLUMNS - 1)) / COLUMNS;

  useLogViewLikedGoodsEdit();

  // 진입 시 navigator 가 extras 로 넘겨준 필터를 route param 으로 받아 사용. iOS FilterInfo 와 동일.
  const { params } = useHomeRoute<'EditGoodsLike'>();
  const { categorySno, isDeliveryFilterOn, order } = params;

  // 진입 필터: 상품 조회 + 삭제 후 같은 query 키로 invalidate 하기 위해 공유.
  const filterParams = useMemo(
    () => ({
      is_fast_delivery: isDeliveryFilterOn ?? false,
      category_sno: categorySno ?? undefined,
      order: order ?? undefined,
    }),
    [isDeliveryFilterOn, categorySno, order],
  );

  const { data, fetchNextPage, hasNextPage, isFetching, isLoading } =
    useLikedGoods(filterParams);

  const list = useMemo<LikedGoodsCardItem[]>(
    () => data?.item_list.map(convertLikedGoodsItemToGoodsData) ?? [],
    [data?.item_list],
  );

  const [selectedSnos, setSelectedSnos] = useState<number[]>([]);

  // 삭제로 list가 줄어들면 selectedSnos에 남아있는 stale sno 제거
  useEffect(() => {
    setSelectedSnos(prev => {
      const visibleSnos = new Set(list.map(entry => entry.item.sno));
      const next = prev.filter(sno => visibleSnos.has(sno));
      return next.length === prev.length ? prev : next;
    });
  }, [list]);

  const toggleSelect = (sno: number) => {
    setSelectedSnos(prev =>
      prev.includes(sno) ? prev.filter(v => v !== sno) : [...prev, sno],
    );
  };

  const rows = useMemo<EditRow[]>(() => {
    const result: EditRow[] = [
      { type: 'header', key: 'header', totalCount: data?.total_count ?? 0 },
    ];
    for (let i = 0; i < list.length; i += COLUMNS) {
      const chunk = list.slice(i, i + COLUMNS);
      result.push({
        type: 'goods',
        key: `goods-${chunk[0].item.sno}`,
        items: chunk,
      });
    }
    return result;
  }, [list, data?.total_count]);

  const { mutateAsync: deleteLikedGoods, isPending: isDeleting } =
    useDeleteLikedGoods({ filter: filterParams });
  const logCompleteRemoveGoods = useLogCompleteRemoveGoods();

  const hasSelection = selectedSnos.length > 0;

  const handleDelete = () => {
    if (!hasSelection) return;
    const snos = [...selectedSnos];
    Alert.alert('상품 삭제', '선택하신 상품을 삭제하시겠어요?', [
      { text: '취소', style: 'cancel' },
      {
        text: '확인',
        onPress: async () => {
          try {
            await deleteLikedGoods({ snos });
            logCompleteRemoveGoods();
            snos.forEach(sno => {
              bridge.likeGoods({ goods_sno: sno, is_liked: false });
            });
            setSelectedSnos([]);
            bridge.toast('상품이 삭제되었습니다');
            // 모두 삭제되어 남은 상품이 없으면 뒤로 가기
            if (list.length === snos.length) {
              onPressBack();
            }
          } catch {
            bridge.toast('삭제에 실패했습니다');
          }
        },
      },
    ]);
  };

  const showFooterSkeleton = hasNextPage || isLoading;

  return (
    <AnalyticsScreen screen={SCREEN}>
      <StackScreen
        title={'전체 상품'}
        hideBackButton={!showBackButton}
        onPressBack={onPressBack}
        bottom={{
          element: (
            <Flex>
              <Flex
                padding={{ horizontal: 12, vertical: 12 }}
                borderTopWidth={1}
                borderColor={'bg_tertiary'}
                backgroundColor={'bg_primary'}
              >
                <PrimaryButton
                  color={'gray'}
                  size={'large'}
                  variant={'fill'}
                  outline
                  state={!hasSelection || isDeleting ? 'disabled' : undefined}
                  onPress={handleDelete}
                >
                  상품 삭제
                </PrimaryButton>
              </Flex>
              <SafeAreaBottomSpacing />
            </Flex>
          ),
        }}
      >
        <Flex flex={1}>
          <List<EditRow>
            data={rows}
            keyExtractor={row => row.key}
            onEndReachedThreshold={1}
            onEndReached={() => {
              if (hasNextPage && !isFetching) fetchNextPage();
            }}
            ListFooterComponent={
              showFooterSkeleton ? (
                <GoodsCardSkeletonList
                  count={6}
                  numColumns={COLUMNS}
                  bottomContentHeight={80}
                />
              ) : null
            }
            renderItem={({ item: row }) => {
              if (row.type === 'header') {
                return (
                  <Flex padding={{ horizontal: 16, vertical: 14 }}>
                    {isLoading ? (
                      <Spinner size={16} />
                    ) : (
                      <Typography type={'body3'} color={'content_tertiary'}>
                        상품 {localizeNumber(row.totalCount)}개
                      </Typography>
                    )}
                  </Flex>
                );
              }
              return (
                <Flex.Row gap={COLUMN_GAP}>
                  {row.items.map(entry => {
                    const sno = entry.item.sno;
                    const isSelected = selectedSnos.includes(sno);
                    return (
                      <Flex key={sno} flex={1}>
                        <GoodsCard
                          data={{
                            ...entry,
                            thumbnail: { ...entry.thumbnail, hideLike: true },
                          }}
                          size={'medium'}
                          defaultWidth={itemWidth}
                          onPress={() => toggleSelect(sno)}
                          onPressLike={async () => {}}
                        />
                        <OverlayCheckBoxView checked={isSelected} />
                      </Flex>
                    );
                  })}
                  {Array.from({ length: COLUMNS - row.items.length }).map(
                    (_, i) => (
                      <Flex key={`pad-${i}`} flex={1} />
                    ),
                  )}
                </Flex.Row>
              );
            }}
          />
        </Flex>
      </StackScreen>
    </AnalyticsScreen>
  );
};
